// useAdminMetrics.js — Firestore query hooks for the admin dashboard.
// Pure helpers (aggregateBy, topNByCost, bucketByDay, fillMissingDays,
// toCSV, downloadCSV, dateNDaysAgo) live in ./useAdminMetrics.helpers.js
// so unit tests can import them without pulling in Firebase.
//
// Hooks:
//   useLLMDailySystemSeries(days)  — daily total cost line series
//   useTwilioDailySystemSeries(days) — same shape, reads twilio_usage_daily
//   useLLMTodayCost()              — real-time sum of llm_usage_log for the
//                                     current UTC day (the daily aggregator
//                                     doesn't write today's rollup until
//                                     ~00:30 UTC the following morning)
//   useTwilioTodayCost()           — real-time sum of twilio_usage_log
//   useLLMByFunction(days)         — function → {calls, cost_usd} aggregated over window
//   useLLMByModel(days)            — model → {calls, cost_usd} aggregated over window
//   useLLMByUser(days, limit)      — top N users by total_cost_usd in window
//   useLLMAlerts(days)             — recent llm_usage_alerts ordered by created_at desc
//   useCallVolumeSeries(days)      — daily call count from collectionGroup('call_history')
//   useCallCountInWindow(days)     — total call count in window (CostPerCall denominator)
//   useActiveUserCount(days)       — distinct uids touching call_history in window
//   useFailureRateSeries(days)     — fraction of call_history outcomes that match the v1 failure prefix
//   useLatencyP95Stats(days)       — median / max of per-call p95 latency from call_metrics
//   useUserNames(uids)             — point-reads users/{uid} for each, returns {namesByUid}

import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../FirebaseConfig';
import {
  dateNDaysAgo,
  aggregateBy,
  topNByCost,
  usageByUid,
  bucketByDay,
  fillMissingDays,
  formatUserName,
} from './useAdminMetrics.helpers';

export {
  dateNDaysAgo,
  aggregateBy,
  topNByCost,
  bucketByDay,
  fillMissingDays,
  formatUserName,
  toCSV,
  downloadCSV,
} from './useAdminMetrics.helpers';

function dateNDaysAgoTimestamp(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  d.setUTCHours(0, 0, 0, 0);
  return Timestamp.fromDate(d);
}

export function useLLMDailySystemSeries(days = 30) {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const start = dateNDaysAgo(days - 1);
    const q = query(
      collection(db, 'llm_usage_daily'),
      where('uid', '==', 'system'),
      where('date', '>=', start),
      orderBy('date', 'asc'),
    );
    getDocs(q)
      .then((snap) => {
        setSeries(
          snap.docs.map((d) => ({
            date: d.data().date,
            cost_usd: d.data().total_cost_usd ?? 0,
            calls: d.data().total_calls ?? 0,
          })),
        );
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  }, [days]);

  const totals = {
    last7: series.slice(-7).reduce((s, r) => s + r.cost_usd, 0),
    last30: series.reduce((s, r) => s + r.cost_usd, 0),
  };

  return { series, totals, loading, error };
}

function _todayStartUtcTimestamp() {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  return Timestamp.fromDate(d);
}

function _useTodayCostFromLog(collectionName) {
  const [today, setToday] = useState({ cost_usd: 0, calls: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const start = _todayStartUtcTimestamp();
    const q = query(
      collection(db, collectionName),
      where('timestamp', '>=', start),
    );
    getDocs(q)
      .then((snap) => {
        let cost = 0;
        let calls = 0;
        snap.forEach((d) => {
          const row = d.data();
          cost += row.cost_usd ?? 0;
          calls += 1;
        });
        setToday({ cost_usd: cost, calls });
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  }, [collectionName]);

  return { today, loading, error };
}

export function useLLMTodayCost() {
  return _useTodayCostFromLog('llm_usage_log');
}

export function useTwilioTodayCost() {
  // `calls` here is leg count, matching the existing TwilioCostTile "Legs"
  // vocabulary — one twilio_usage_log row is one billable leg.
  return _useTodayCostFromLog('twilio_usage_log');
}

export function useTwilioDailySystemSeries(days = 30) {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const start = dateNDaysAgo(days - 1);
    const q = query(
      collection(db, 'twilio_usage_daily'),
      where('uid', '==', 'system'),
      where('date', '>=', start),
      orderBy('date', 'asc'),
    );
    getDocs(q)
      .then((snap) => {
        setSeries(
          snap.docs.map((d) => ({
            date: d.data().date,
            cost_usd: d.data().total_cost_usd ?? 0,
            calls: d.data().total_calls ?? 0,
            legs: d.data().total_legs ?? 0,
          })),
        );
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  }, [days]);

  const totals = {
    last7: series.slice(-7).reduce((s, r) => s + r.cost_usd, 0),
    last30: series.reduce((s, r) => s + r.cost_usd, 0),
    legs7: series.slice(-7).reduce((s, r) => s + r.legs, 0),
  };

  return { series, totals, loading, error };
}

export function useLLMByFunction(days = 30) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const start = dateNDaysAgo(days - 1);
    const q = query(
      collection(db, 'llm_usage_daily'),
      where('uid', '==', 'system'),
      where('date', '>=', start),
    );
    getDocs(q)
      .then((snap) => {
        const docs = snap.docs.map((d) => d.data());
        const agg = aggregateBy(docs, 'by_function');
        setRows(
          Object.entries(agg)
            .map(([fn, stats]) => ({ name: fn, ...stats }))
            .sort((a, b) => b.cost_usd - a.cost_usd),
        );
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  }, [days]);

  return { rows, loading, error };
}

export function useLLMByModel(days = 30) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const start = dateNDaysAgo(days - 1);
    const q = query(
      collection(db, 'llm_usage_daily'),
      where('uid', '==', 'system'),
      where('date', '>=', start),
    );
    getDocs(q)
      .then((snap) => {
        const docs = snap.docs.map((d) => d.data());
        const agg = aggregateBy(docs, 'by_model');
        setRows(
          Object.entries(agg)
            .map(([model, stats]) => ({ name: model, ...stats }))
            .sort((a, b) => b.cost_usd - a.cost_usd),
        );
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  }, [days]);

  return { rows, loading, error };
}

// Reads ALL llm_usage_daily docs in the window (per-user + system),
// filters out system client-side, groups by uid, returns top N.
export function useLLMByUser(days = 30, limit = 10) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const start = dateNDaysAgo(days - 1);
    const q = query(
      collection(db, 'llm_usage_daily'),
      where('date', '>=', start),
    );
    getDocs(q)
      .then((snap) => {
        const docs = snap.docs.map((d) => d.data());
        setRows(topNByCost(docs, limit));
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  }, [days, limit]);

  return { rows, loading, error };
}

// LLM usage aggregated per uid over the window, keyed for O(1) lookup.
// Shape: { byUid: { [uid]: { calls, cost_usd } }, loading, error }.
// Unlike useLLMByUser this returns the full map (no topN slice) so callers
// can join it against an arbitrary list of users — e.g. the Users tab rows.
export function useLLMUsageByAllUsers(days = 30) {
  const [byUid, setByUid] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const start = dateNDaysAgo(days - 1);
    const q = query(
      collection(db, 'llm_usage_daily'),
      where('date', '>=', start),
    );
    getDocs(q)
      .then((snap) => {
        const docs = snap.docs.map((d) => d.data());
        setByUid(usageByUid(docs));
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  }, [days]);

  return { byUid, loading, error };
}

export function useLLMAlerts(days = 7) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const since = dateNDaysAgoTimestamp(days);
    const q = query(
      collection(db, 'llm_usage_alerts'),
      where('created_at', '>=', since),
      orderBy('created_at', 'desc'),
    );
    getDocs(q)
      .then((snap) => {
        setAlerts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  }, [days]);

  return { alerts, loading, error };
}

export function useCallVolumeSeries(days = 30) {
  const [series, setSeries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const since = dateNDaysAgoTimestamp(days - 1);
    const q = query(
      collectionGroup(db, 'call_history'),
      where('timestamp', '>=', since),
    );
    getDocs(q)
      .then((snap) => {
        const timestamps = snap.docs.map((d) => d.data().timestamp).filter(Boolean);
        const raw = bucketByDay(timestamps);
        setSeries(fillMissingDays(raw, days));
        setTotal(snap.size);
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  }, [days]);

  return { series, total, loading, error };
}

export function useCallCountInWindow(days = 7) {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const since = dateNDaysAgoTimestamp(days - 1);
    const q = query(
      collectionGroup(db, 'call_history'),
      where('timestamp', '>=', since),
    );
    getDocs(q)
      .then((snap) => {
        setCount(snap.size);
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  }, [days]);

  return { count, loading, error };
}

// Failure rate over the window. v1 definition of "failure":
// `outcome` starts with "Call ended before any response" — the explicit case
// the voice agent writes when a call_history record is saved but progress_log
// is empty (server.py:1035, "Save an entry even when progress_log is empty so
// users can still see and retry the instructions for a call that failed before
// Maia produced any transcripts"). Other failure modes (carrier no-answer,
// busy, STIR-B reject) don't reach call_history today and aren't counted.
const FAILED_OUTCOME_PREFIX = 'Call ended before any response';

export function useFailureRateSeries(days = 7) {
  const [stats, setStats] = useState({ failed: 0, total: 0, rate: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const since = dateNDaysAgoTimestamp(days - 1);
    const q = query(
      collectionGroup(db, 'call_history'),
      where('timestamp', '>=', since),
    );
    getDocs(q)
      .then((snap) => {
        let failed = 0;
        for (const d of snap.docs) {
          const outcome = d.data().outcome || '';
          if (outcome.startsWith(FAILED_OUTCOME_PREFIX)) failed += 1;
        }
        const total = snap.size;
        setStats({ failed, total, rate: total ? failed / total : 0 });
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  }, [days]);

  return { ...stats, loading, error };
}

// Per-call p95 latency aggregated over the window. Reads `call_metrics`
// (top-level collection, one doc per completed call). Returns:
//   medianP95Ms — median of per-call p95 latencies (the typical call's p95)
//   maxP95Ms    — worst per-call p95 in the window (the outlier)
//   sampleSize  — count of call_metrics docs read
// Empty window returns zeros; consumers render "no data".
export function useLatencyP95Stats(days = 7) {
  const [stats, setStats] = useState({ medianP95Ms: 0, maxP95Ms: 0, sampleSize: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const since = dateNDaysAgoTimestamp(days - 1);
    const q = query(
      collection(db, 'call_metrics'),
      where('timestamp', '>=', since),
    );
    getDocs(q)
      .then((snap) => {
        const p95s = snap.docs
          .map((d) => d.data().p95_latency_ms)
          .filter((v) => typeof v === 'number');
        if (p95s.length === 0) {
          setStats({ medianP95Ms: 0, maxP95Ms: 0, sampleSize: 0 });
        } else {
          const sorted = [...p95s].sort((a, b) => a - b);
          const median = sorted[Math.floor((sorted.length - 1) / 2)];
          const max = sorted[sorted.length - 1];
          setStats({ medianP95Ms: median, maxP95Ms: max, sampleSize: p95s.length });
        }
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  }, [days]);

  return { ...stats, loading, error };
}

// Distinct uids touching call_history in the window. uid is derived from the
// parent doc ID — call_records don't carry a uid field of their own.
export function useActiveUserCount(days = 7) {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const since = dateNDaysAgoTimestamp(days - 1);
    const q = query(
      collectionGroup(db, 'call_history'),
      where('timestamp', '>=', since),
    );
    getDocs(q)
      .then((snap) => {
        const uids = new Set();
        for (const d of snap.docs) {
          const uid = d.ref.parent.parent?.id;
          if (uid) uids.add(uid);
        }
        setCount(uids.size);
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
  }, [days]);

  return { count, loading, error };
}

// Resolves uids to display labels by point-reading users/{uid} once per uid.
// Returns { namesByUid: { [uid]: "First Last" | email | "uid…" }, loading, error }.
// Empty uids array short-circuits — no I/O. Pass a stable array ref (e.g. memoize
// in the caller) so the effect only re-runs when the uid set actually changes.
export function useUserNames(uids) {
  // Stable join key — string compare is the cheapest way to dedupe and order.
  const key = useMemo(() => [...new Set(uids)].sort().join(','), [uids]);
  const [namesByUid, setNamesByUid] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const list = key ? key.split(',') : [];
    if (list.length === 0) {
      setNamesByUid({});
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all(
      list.map((uid) =>
        getDoc(doc(db, 'users', uid))
          .then((snap) => [uid, formatUserName(snap.exists() ? snap.data() : null, uid)])
          .catch(() => [uid, formatUserName(null, uid)]),
      ),
    )
      .then((entries) => {
        if (cancelled) return;
        setNamesByUid(Object.fromEntries(entries));
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [key]);

  return { namesByUid, loading, error };
}
